import type { AccountType } from "../../../shared/types/accounts";
import { ACCOUNT_DISCONNECTED_MSG } from "../../../constants/TooltipMessages";
import { accountIsBrokenAtAccountLevel } from "../../../utils/account-health";
import { isInstagramPhoneStateIgnored } from "../../../entities/accounts/libs/get-account-statuses";
import type { SelectorAccount, SelectorGroup } from "./types";

/** How a picker judges whether an account needs reconnecting. SKE-5376. */
export interface SelectorAccountHealthOptions {
  /**
   * `useInstagramApiPublishingOn()`. Required, so every picker says where the
   * gate came from: an Instagram account on the API track is judged on its
   * API connection, never on its phone login.
   */
  instagramApiPublishing: boolean;
  /** Inbox pickers: an Instagram account also needs the messaging permission. */
  inbox?: boolean;
}

type LegacyAccountNameFields = {
  name?: string;
  first_name?: string;
  last_name?: string;
};

function selectorAccountLabel(account: AccountType): string {
  const login = account.loginAlias || account.login;
  if (login) {
    const parts = login.split("->");
    return (parts.length === 2 ? parts[1] : login).trim();
  }
  if (account.displayLogin) return account.displayLogin;

  const legacyAccount = account as AccountType & LegacyAccountNameFields;
  const legacyFullName = [legacyAccount.first_name, legacyAccount.last_name]
    .filter(Boolean)
    .join(" ")
    .trim();

  return legacyFullName || legacyAccount.name || account._id;
}

function selectorAccountGroup(account: AccountType): string | undefined {
  const aliasParts = account.loginAlias?.split("->");
  const aliasGroup =
    aliasParts && aliasParts.length > 1 ? aliasParts[0].trim() : undefined;
  return aliasGroup || account.brandName || undefined;
}

/**
 * The muted second line on an account row: the platform handle, without the
 * "<Group> -> " prefix the alias carries. Returns nothing when it would only
 * repeat the label. A Facebook page whose handle is its own name would
 * otherwise render twice.
 */
export function selectorAccountHandle(
  account: AccountType,
  label?: string
): string | undefined {
  const raw = account.displayLogin || account.login;
  if (!raw) return undefined;

  const parts = raw.split("->");
  const handle = (parts.length === 2 ? parts[1] : raw).trim();
  if (!handle) return undefined;

  const resolvedLabel = label ?? selectorAccountLabel(account);
  if (handle.toLowerCase() === resolvedLabel.trim().toLowerCase()) {
    return undefined;
  }

  // A handle with whitespace is a display name, not an @handle.
  if (handle.startsWith("@") || /\s/.test(handle)) return handle;
  return `@${handle}`;
}

const FACEBOOK_POSTING_SCOPES = [
  "pages_show_list",
  "pages_manage_posts",
  "pages_manage_metadata",
  "pages_read_engagement",
  "pages_read_user_content",
  "business_management",
];

function selectorAccountIsDisconnected(
  account: AccountType,
  options: SelectorAccountHealthOptions
): boolean {
  const phoneStateIgnored = isInstagramPhoneStateIgnored(
    account,
    options.instagramApiPublishing
  );
  if (accountIsBrokenAtAccountLevel(account, phoneStateIgnored)) {
    return true;
  }

  const scopes = account.session?.scopes || [];
  const platformType = account.platformType || "IG";

  if (platformType === "FB" && !account.isGroup) {
    return ![
      ...FACEBOOK_POSTING_SCOPES,
      "read_insights",
      "pages_messaging",
    ].every((scope) => scopes.includes(scope));
  }

  if (platformType === "IG") {
    if (!account.igTokenAddedAt) return true;
    if (phoneStateIgnored && !options.inbox) return false;
    return !scopes.includes("instagram_manage_messages");
  }

  return false;
}

export function accountTypeToSelectorAccount(
  account: AccountType,
  options: SelectorAccountHealthOptions
): SelectorAccount {
  const group = selectorAccountGroup(account);
  const disconnected = selectorAccountIsDisconnected(account, options);
  const label = selectorAccountLabel(account);
  return {
    id: account._id,
    label,
    avatarUrl: account.profilePictureUrl,
    platformType: account.platformType || account.platformName,
    color: account.color,
    groupId: group,
    groupLabel: group,
    secondaryText: selectorAccountHandle(account, label),
    searchText: [account.login, account.loginAlias, account.displayLogin]
      .filter(Boolean)
      .join(" "),
    disconnected,
    attentionReason: disconnected ? ACCOUNT_DISCONNECTED_MSG : undefined,
  };
}

export function accountTypesToSelectorAccounts(
  accounts: AccountType[],
  options: SelectorAccountHealthOptions
): SelectorAccount[] {
  return accounts.map((account) =>
    accountTypeToSelectorAccount(account, options)
  );
}

export function selectorAccountsToSelectorGroups(
  accounts: SelectorAccount[]
): SelectorGroup[] {
  const groups = new Map<string, SelectorAccount[]>();
  accounts.forEach((account) => {
    if (!account.groupLabel) return;
    const members = groups.get(account.groupLabel) || [];
    members.push(account);
    groups.set(account.groupLabel, members);
  });
  return Array.from(groups.entries()).map(([label, members]) => ({
    id: label,
    label,
    accounts: members,
  }));
}

export function accountTypesToSelectorGroups(
  accounts: AccountType[],
  options: SelectorAccountHealthOptions
): SelectorGroup[] {
  return selectorAccountsToSelectorGroups(
    accountTypesToSelectorAccounts(accounts, options)
  );
}
